import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { Space } from 'antd';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { ReactNode, memo, useMemo } from 'react';
import { ReactComponent as AliPayIcon } from 'assets/pay/alipay.svg';
import { ReactComponent as WechatPayIcon } from 'assets/pay/wechat.svg';
import { ReactComponent as AirwallexPayIcon } from 'assets/pay/airwallex.svg';
import pingpongImage from 'assets/pay/pingpong.png';
import lianlianImage from 'assets/pay/lianlian.png';

import CheckContainer from './CheckContainer';
import { usePaySupportFlagrValue } from 'hooks/business/system/useFlagr';
import { useTranslate } from '@tolgee/react';

const payTypeConfigsMap: Record<
  Pay.PayType,
  {
    icon: ReactNode;
    type: Pay.PayType;
  } & Util.RequireAtLeastOne<{
    label: string;
    i18nKey: string;
  }>
> = {
  airwallex: {
    icon: <Icon component={AirwallexPayIcon} />,
    type: 'airwallex',
    label: 'Airwallex',
  },
  pingpong: {
    icon: <Icon component={() => <img src={pingpongImage} alt="pingpong" />} />,
    type: 'pingpong',
    label: 'PingPong',
  },
  lianlian: {
    icon: <Icon component={() => <img src={lianlianImage} alt="lianlian" />} />,
    type: 'lianlian',
    label: 'LianLian',
  },
  alipay: {
    icon: <Icon component={AliPayIcon} />,
    type: 'alipay',
    i18nKey: 'translation.支付宝',
  },
  wxpay: {
    icon: <Icon component={WechatPayIcon} />,
    type: 'wxpay',
    i18nKey: 'translation.微信',
  },
} as const;
export interface PayTypeCheckGroupProps {
  className?: string;
  dataSource: Pay.PayType[];
  value: Pay.PayType;
  onChange?: (value: Pay.PayType) => void;
  currency: General.Currency;
  bank: Pay.PingpongBank;
  onBankChange: (value: Pay.PingpongBank) => void;
}

const PayTypeCheckGroup = (props: PayTypeCheckGroupProps) => {
  const { dataSource, value, onChange, currency, bank, onBankChange, ...otherProps } = props;
  const supportConfig = usePaySupportFlagrValue();
  const { t } = useTranslate();

  const { airwallexEnable } = useOrgConfigStateValue();
  const payTypeSelect = (
    <Ul>
      {dataSource.map((payment) => {
        const config = payTypeConfigsMap[payment];
        const text = config.i18nKey ? t(config.i18nKey) : config.label;
        if (payment === 'airwallex' && !airwallexEnable) {
          return null;
        }
        const supportKey = `${config.type}-${currency}`;
        return (
          <li key={payment}>
            <CheckContainer
              checked={payment === value}
              onClick={() => {
                onChange?.(payment);
              }}
            >
              <Space align="center">
                {config.icon}
                {text}
              </Space>
              {supportConfig?.[supportKey] && (
                <div className="text-[#84868c] text-[12px] mt-[4px]">
                  {t('paysupport_x', {
                    x: supportConfig?.[supportKey]
                      .join('、')
                      .replace(/BankCard/g, t('paysupport_bankcard')),
                  })}
                </div>
              )}
            </CheckContainer>
          </li>
        );
      })}
    </Ul>
  );

  const bankSelect = useMemo(() => {
    if (value === 'pingpong' && currency === 'IDR') {
      const bankList: Pay.PingpongBank[] = [
        'BCA',
        'BNI',
        'BRI',
        'CIMB',
        'Mandiri',
        'Permata',
        'ShopeePay',
      ];

      return (
        <Ul>
          {bankList.map((cur) => {
            return (
              <li key={cur}>
                <CheckContainer
                  checked={cur === bank}
                  onChange={(checked) => checked && onBankChange(cur)}
                >
                  {cur}
                </CheckContainer>
              </li>
            );
          })}
        </Ul>
      );
    }
  }, [bank, onBankChange, value, currency]);

  return (
    <div {...otherProps}>
      {payTypeSelect}
      {bankSelect}
    </div>
  );
};

export default memo(PayTypeCheckGroup);

const Ul = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  li {
    + li {
      margin-left: 20px;
    }
    .anticon > img {
      width: 16px;
      margin-top: -4px;
    }
  }
`;
